/**
 * Campus Correspondent — single-page dashboard for one campus.
 *
 * No sidebar. Header shows the campus name + the correspondent's avatar
 * (with Change Password + Logout from the existing avatar dropdown).
 * Body uses a tabbed layout:
 *
 *   • Overview   – KPI tiles + revenue chart (live from /api/campuses/{id}/analytics)
 *   • Stores     – stores on this campus + revenue rollup
 *   • Royalties  – read-only ledger
 *   • Utilities  – utility bills (meter readings) for this campus
 *   • Payments   – utility invoices (raise + track status: issued / sent / paid)
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useOnboarding } from '../components/onboarding/OnboardingProvider';
import { API_URL, formatApiError } from '../utils/api';
import { Button } from '../components/ui/button';
import RoyaltyPanel from '../components/finance/RoyaltyPanel';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui/tabs';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem,
} from '../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  ChartPieSlice, Storefront, Receipt, Money, Drop, LockKey, SignOut,
  CurrencyDollar, ChartLineUp, GraduationCap, Plus, Spinner, WarningCircle,
  CheckCircle, Clock, Drop as DropIcon, Lightning, Leaf, Recycle, Cloud,
  UsersThree, GenderIntersex, Compass, FilePdf,
} from '@phosphor-icons/react';
import {
  ResponsiveContainer as ReResponsiveContainer,
  BarChart as ReBarChart, Bar as ReBar, XAxis as ReXAxis, YAxis as ReYAxis,
  CartesianGrid as ReCartesianGrid, Tooltip as ReTooltip, Cell as ReCell,
  AreaChart as ReAreaChart, Area as ReArea,
  LineChart as ReLineChart, Line as ReLine,
  PieChart as RePieChart, Pie as RePie, Legend as ReLegend,
} from 'recharts';

/* ---------- helpers ---------- */
const authHeaders = () => {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const fmt   = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtI  = (n) => Number(n || 0).toLocaleString();
const compact = (n) => {
  const v = Math.abs(Number(n || 0));
  if (v >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return String(Math.round(n || 0));
};

const tooltipStyle = {
  fontFamily: 'Plus Jakarta Sans, sans-serif',
  border: '1px solid #EDE6D8',
  borderRadius: '10px',
  fontSize: 12,
  boxShadow: '0 4px 16px -8px rgba(28,25,23,0.12)',
};

/* ---------- Skeleton primitives (shimmer wave) ---------- */
const SK_BASE =
  "relative overflow-hidden bg-[#EDE6D8]/60 before:absolute before:inset-0 " +
  "before:-translate-x-full before:bg-gradient-to-r " +
  "before:from-transparent before:via-white/55 before:to-transparent " +
  "before:animate-[shimmer_1.6s_infinite]";

function SkelBlock({ className = '', testId }) {
  return <div className={`${SK_BASE} rounded-md ${className}`} data-testid={testId} />;
}

function SkelCard({ children, className = '', testId }) {
  return (
    <div className={`bg-white border border-[#EDE6D8] rounded-2xl p-4 ${className}`} data-testid={testId}>
      {children}
    </div>
  );
}

function OverviewSkeleton() {
  return (
    <div className="space-y-4" data-testid="cc-overview-skeleton">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkelCard key={i}>
            <div className="flex items-center justify-between mb-3">
              <SkelBlock className="h-3 w-24" />
              <SkelBlock className="h-7 w-7 rounded-lg" />
            </div>
            <SkelBlock className="h-7 w-28" />
          </SkelCard>
        ))}
      </div>
      <SkelCard className="p-5">
        <SkelBlock className="h-4 w-44 mb-4" />
        <SkelBlock className="h-[220px] w-full" />
      </SkelCard>
      <SkelCard className="p-5">
        <SkelBlock className="h-4 w-44 mb-4" />
        <SkelBlock className="h-[200px] w-full" />
      </SkelCard>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        <div className="lg:col-span-5 grid grid-cols-2 gap-3">
          <SkelCard><SkelBlock className="h-3 w-20 mb-3" /><SkelBlock className="h-8 w-24" /></SkelCard>
          <SkelCard><SkelBlock className="h-3 w-20 mb-3" /><SkelBlock className="h-8 w-24" /></SkelCard>
        </div>
        <SkelCard className="lg:col-span-7 p-5">
          <SkelBlock className="h-4 w-32 mb-4" />
          <SkelBlock className="h-[180px] w-full rounded-full" />
        </SkelCard>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <SkelCard className="p-5"><SkelBlock className="h-4 w-32 mb-4" /><SkelBlock className="h-20 w-full" /></SkelCard>
        <SkelCard className="p-5"><SkelBlock className="h-4 w-32 mb-4" /><SkelBlock className="h-20 w-full" /></SkelCard>
      </div>
      <SkelCard className="p-5">
        <SkelBlock className="h-4 w-44 mb-4" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SkelBlock className="h-24 w-full" />
          <SkelBlock className="h-24 w-full" />
          <SkelBlock className="h-24 w-full" />
        </div>
      </SkelCard>
    </div>
  );
}

function StoreInsightsSkeleton() {
  return (
    <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5 space-y-3" data-testid="cc-store-insights-skeleton">
      <SkelBlock className="h-4 w-40" />
      <div className="grid grid-cols-2 gap-3">
        <SkelBlock className="h-20 w-full" />
        <SkelBlock className="h-20 w-full" />
        <SkelBlock className="h-20 w-full" />
        <SkelBlock className="h-20 w-full" />
      </div>
    </div>
  );
}

/* ---------- tiny KPI card ---------- */
function Kpi({ label, value, unit, icon: Icon, accent = '#0A2540' }) {
  return (
    <div className="bg-white border border-[#EDE6D8] rounded-2xl p-4">
      <div className="flex items-center justify-between mb-2">
        <p
          className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold"
          style={{ fontFamily: 'JetBrains Mono, monospace' }}
        >
          {label}
        </p>
        <span
          className="w-7 h-7 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${accent}15`, color: accent }}
        >
          <Icon size={14} weight="fill" />
        </span>
      </div>
      <p
        className="text-xl font-bold text-[#1A1D1C] truncate"
        style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
      >
        {value}
        {unit ? <span className="text-[10px] ml-1 text-[#78716C] font-medium">{unit}</span> : null}
      </p>
    </div>
  );
}

/* ---------- Overview ---------- */
function OverviewTab({ campusId, currency }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/campuses/${campusId}/analytics`, { headers: authHeaders() });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e, 'Failed to load campus analytics'));
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [campusId]);

  if (loading) return <OverviewSkeleton />;
  if (!data) return <p className="py-16 text-center text-sm text-[#78716C]">No analytics available.</p>;

  const h = data.headline || {};
  const monthly = data.monthly || [];
  const daily   = data.daily   || [];

  // Royalty KPI cards — paired with their revenue counterparts as a sublabel
  // so the correspondent can see both at a glance (revenue = refills).
  // 4 independent royalty cards
  const royaltyCards = [
    { label: 'Royalty · Yesterday',  value: h.yesterday_royalty, icon: CurrencyDollar, accent: '#0A2540', sub: h.yesterday_date || null },
    { label: 'Royalty · This Month', value: h.month_royalty,    icon: ChartLineUp,    accent: '#059669' },
    { label: 'Royalty · This Year',  value: h.year_royalty,     icon: ChartPieSlice,  accent: '#7C3AED' },
    { label: 'Royalty · Lifetime',   value: h.lifetime_royalty, icon: Money,          accent: '#C9A35B' },
  ];

  const hasMonthlyData = monthly.some((m) => (m.royalty || 0) > 0);
  const hasDailyData   = daily.some((d2) => (d2.royalty || 0) > 0);

  const gender = data.gender_breakdown || { M: 0, F: 0, O: 0 };
  const genderTotal = (gender.M || 0) + (gender.F || 0) + (gender.O || 0);
  const genderData = [
    { name: 'Male',   value: gender.M || 0, color: '#0A2540' },
    { name: 'Female', value: gender.F || 0, color: '#C9A35B' },
    ...((gender.O || 0) > 0 ? [{ name: 'Other', value: gender.O, color: '#78716C' }] : []),
  ];

  const catBreakdown = data.category_breakdown || {};
  const categoryData = Object.entries(catBreakdown)
    .map(([k, v]) => ({ name: k, value: v }))
    .sort((a, b) => b.value - a.value);
  const catMax = categoryData.reduce((mx, c) => Math.max(mx, c.value), 0) || 1;
  const catColors = ['#0A2540', '#C9A35B', '#059669', '#7C3AED', '#EF4444', '#0EA5E9'];

  const u = data.utility_summary || { water: {}, electricity: {} };
  const impact = data.impact || {};

  return (
    <div className="space-y-4">
      {/* 4 royalty KPI cards (each independent) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {royaltyCards.map((c) => (
          <div key={c.label} className="bg-white border border-[#EDE6D8] rounded-2xl p-4" data-testid={`cc-kpi-${c.label}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold"
                 style={{ fontFamily: 'JetBrains Mono, monospace' }}>{c.label}</p>
              <span className="w-7 h-7 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${c.accent}15`, color: c.accent }}>
                <c.icon size={14} weight="fill" />
              </span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <p className="text-xl font-bold text-[#1A1D1C] truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {fmt(c.value)}
              </p>
              {currency ? <span className="text-[10px] text-[#78716C] font-medium">{currency}</span> : null}
            </div>
            {c.sub && (
              <p className="mt-1 text-[10px] text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {c.sub}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Royalty · Past 6 months */}
      <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#0A2540]/10 text-[#0A2540]"><Receipt size={14} weight="fill" /></span>
            <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              Royalty · Past 6 Months
            </p>
          </div>
          <p className="text-[11px] font-mono text-[#78716C]">in {currency || '—'}</p>
        </div>
        {!hasMonthlyData ? (
          <div className="h-[220px] flex items-center justify-center"><p className="text-xs text-[#78716C]">No royalty earned in the past 6 months.</p></div>
        ) : (
          <div className="h-[240px]">
            <ReResponsiveContainer width="100%" height="100%">
              <ReBarChart data={monthly} margin={{ top: 16, right: 8, left: -8, bottom: 0 }}>
                <ReCartesianGrid strokeDasharray="3 3" stroke="#EDE6D8" vertical={false} />
                <ReXAxis dataKey="label" tick={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }} stroke="#78716C" axisLine={false} tickLine={false} />
                <ReYAxis tick={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }} stroke="#78716C" axisLine={false} tickLine={false} tickFormatter={compact} />
                <ReTooltip contentStyle={tooltipStyle} labelStyle={{ color: '#0A2540', fontWeight: 600 }} formatter={(v) => [`${fmt(v)} ${currency}`, 'Royalty']} />
                <ReBar dataKey="royalty" radius={[8, 8, 0, 0]} maxBarSize={72}>
                  {monthly.map((_, i) => (<ReCell key={i} fill={i === monthly.length - 1 ? '#0A2540' : '#C9A35B'} />))}
                </ReBar>
              </ReBarChart>
            </ReResponsiveContainer>
          </div>
        )}
      </div>

      {/* Daily royalty · 30 days */}
      <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#C9A35B]/15 text-[#C9A35B]"><ChartLineUp size={14} weight="fill" /></span>
          <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            Daily Royalty · Past 30 Days
          </p>
        </div>
        {!hasDailyData ? (
          <div className="h-[200px] flex items-center justify-center"><p className="text-xs text-[#78716C]">No royalty earned in the last 30 days.</p></div>
        ) : (
          <div className="h-[220px]">
            <ReResponsiveContainer width="100%" height="100%">
              <ReAreaChart data={daily} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="ccRoyGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C9A35B" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#C9A35B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <ReCartesianGrid strokeDasharray="3 3" stroke="#EDE6D8" vertical={false} />
                <ReXAxis dataKey="label" tick={{ fontSize: 9, fontFamily: 'JetBrains Mono, monospace' }} stroke="#78716C" axisLine={false} tickLine={false} interval={3} />
                <ReYAxis tick={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }} stroke="#78716C" axisLine={false} tickLine={false} tickFormatter={compact} />
                <ReTooltip contentStyle={tooltipStyle} formatter={(v) => [`${fmt(v)} ${currency}`, 'Royalty']} labelStyle={{ color: '#0A2540', fontWeight: 600 }} />
                <ReArea type="monotone" dataKey="royalty" stroke="#C9A35B" strokeWidth={2} fill="url(#ccRoyGrad)" />
              </ReAreaChart>
            </ReResponsiveContainer>
          </div>
        )}
      </div>

      {/* Subscribers & Stores stats */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* Counts column — 2 cards stacked */}
        <div className="lg:col-span-5 grid grid-cols-2 gap-3">
          <div className="bg-white border border-[#EDE6D8] rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Total Subscribers
              </p>
              <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#7C3AED]/15 text-[#7C3AED]">
                <UsersThree size={14} weight="fill" />
              </span>
            </div>
            <p className="text-3xl font-bold text-[#1A1D1C]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {fmtI(h.total_subscribers)}
            </p>
            <p className="text-[10px] text-[#78716C] mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {fmtI(h.active_subscribers)} active
            </p>
          </div>

          <div className="bg-white border border-[#EDE6D8] rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Total Stores
              </p>
              <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#059669]/15 text-[#059669]">
                <Storefront size={14} weight="fill" />
              </span>
            </div>
            <p className="text-3xl font-bold text-[#1A1D1C]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {fmtI(h.total_stores)}
            </p>
            <p className="text-[10px] text-[#78716C] mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {fmtI(h.operating_stores)} operating
            </p>
          </div>
        </div>

        {/* Gender donut */}
        <div className="lg:col-span-7 bg-white border border-[#EDE6D8] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#0A2540]/10 text-[#0A2540]">
              <GenderIntersex size={14} weight="fill" />
            </span>
            <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              Gender Distribution
            </p>
          </div>
          {genderTotal === 0 ? (
            <div className="h-[200px] flex items-center justify-center"><p className="text-xs text-[#78716C]">No subscribers yet.</p></div>
          ) : (
            <div className="h-[220px]">
              <ReResponsiveContainer width="100%" height="100%">
                <RePieChart>
                  <RePie
                    data={genderData}
                    cx="50%" cy="50%"
                    innerRadius={48} outerRadius={78}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                  >
                    {genderData.map((entry, i) => (<ReCell key={i} fill={entry.color} />))}
                  </RePie>
                  <ReTooltip contentStyle={tooltipStyle} formatter={(v, n) => [`${fmtI(v)} (${((v / genderTotal) * 100).toFixed(1)}%)`, n]} />
                  <ReLegend
                    verticalAlign="bottom"
                    iconType="circle"
                    wrapperStyle={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: '#78716C' }}
                  />
                </RePieChart>
              </ReResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Utility cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <UtilityCard
          icon={DropIcon}
          accent="#0EA5E9"
          title="Water Usage"
          data={u.water}
          currency={currency}
        />
        <UtilityCard
          icon={Lightning}
          accent="#F59E0B"
          title="Electricity Usage"
          data={u.electricity}
          currency={currency}
        />
      </div>

      {/* Impact Insights — HIDDEN until computation rates are confirmed.
          To re-enable: uncomment the block below. */}
      {false && (
      <div className="bg-gradient-to-br from-[#059669]/5 to-[#0A2540]/5 border border-[#059669]/20 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-xl flex items-center justify-center bg-[#059669]/20 text-[#059669]">
              <Leaf size={16} weight="fill" />
            </span>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Impact Insights
              </p>
              <p className="text-sm font-semibold text-[#1A1D1C]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Cumulative environmental &amp; community impact
              </p>
            </div>
          </div>
          <p className="text-[9px] text-[#78716C] hidden md:block" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            Based on {fmtI(impact.lifetime_volume_l)} L refilled
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <ImpactTile
            icon={Recycle}
            accent="#0EA5E9"
            label="Single-use Plastics Avoided"
            value={fmtI(impact.bottles_avoided)}
            unit="bottles"
            note="Assumes 500 ml bottles"
          />
          <ImpactTile
            icon={Cloud}
            accent="#059669"
            label="Carbon Mitigated"
            value={fmt(impact.co2_kg_avoided)}
            unit="kg CO₂e"
            note="≈ 82 g per bottle"
          />
          <ImpactTile
            icon={CurrencyDollar}
            accent="#C9A35B"
            label="Money Saved by Users"
            value={fmt(impact.money_saved)}
            unit={currency}
            note={`vs. ${fmt(impact.assumptions?.retail_per_l)} ${currency}/L bottled`}
          />
        </div>
      </div>
      )}
    </div>
  );
}

/* ---------- Utility card ---------- */
function UtilityCard({ icon: Icon, accent, title, data, currency }) {
  const last = data?.last_month_usage || 0;
  const avg  = data?.avg_6m_usage || 0;
  const delta = avg > 0 ? ((last - avg) / avg) * 100 : 0;
  const trendUp = delta >= 0;
  return (
    <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${accent}15`, color: accent }}>
            <Icon size={16} weight="fill" />
          </span>
          <p className="text-sm font-semibold text-[#1A1D1C]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {title}
          </p>
        </div>
        {avg > 0 && (
          <span className={`text-[10px] uppercase tracking-widest font-semibold ${trendUp ? 'text-amber-600' : 'text-[#10B981]'}`}
                style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {trendUp ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            Last Month
          </p>
          <p className="text-xl font-bold text-[#1A1D1C] mt-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {fmt(last)} <span className="text-xs text-[#78716C] font-medium">{data?.unit}</span>
          </p>
          <p className="text-[10px] text-[#78716C] mt-0.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {fmt(data?.last_month_amount)} {currency || ''}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            Avg · Past 6 mo
          </p>
          <p className="text-xl font-bold text-[#1A1D1C] mt-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {fmt(avg)} <span className="text-xs text-[#78716C] font-medium">{data?.unit}</span>
          </p>
          <p className="text-[10px] text-[#78716C] mt-0.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {fmt(data?.avg_6m_amount)} {currency || ''}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ---------- Impact tile ---------- */
function ImpactTile({ icon: Icon, accent, label, value, unit, note }) {
  return (
    <div className="bg-white/70 backdrop-blur-sm border border-[#EDE6D8] rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {label}
        </p>
        <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}15`, color: accent }}>
          <Icon size={14} weight="fill" />
        </span>
      </div>
      <p className="text-2xl font-bold text-[#1A1D1C]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
        {value} <span className="text-xs text-[#78716C] font-medium">{unit}</span>
      </p>
      {note && (
        <p className="text-[10px] text-[#78716C] mt-1.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {note}
        </p>
      )}
    </div>
  );
}

/* ---------- Store Insights Panel (right side, when a store is selected) ---------- */
function StoreInsightsPanel({ storeId, currency, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/stores/${storeId}/analytics`, { headers: authHeaders() });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e, 'Failed to load store analytics'));
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [storeId]);

  if (loading) return <StoreInsightsSkeleton />;
  if (!data) {
    return (
      <div className="bg-white border border-[#EDE6D8] rounded-2xl p-6 text-center">
        <p className="text-xs text-[#78716C]">No insights data for this store.</p>
        <button type="button" onClick={onClose} className="mt-3 text-xs text-[#0A2540] underline">Back to logs</button>
      </div>
    );
  }

  const todayAmt = data.today?.amount || 0;
  const yAmt = data.yesterday?.amount || 0;
  const delta = yAmt > 0 ? ((todayAmt - yAmt) / yAmt) * 100 : (todayAmt > 0 ? 100 : 0);
  const deltaUp = delta >= 0;
  const monthAmt = data.month?.amount || 0;
  const lmAmt = data.last_month?.amount || 0;
  const monthDelta = lmAmt > 0 ? ((monthAmt - lmAmt) / lmAmt) * 100 : (monthAmt > 0 ? 100 : 0);
  const monthDeltaUp = monthDelta >= 0;
  const series30 = (data.daily_30 || []).map((d) => ({ ...d }));
  const series7  = (data.daily_sales || []).map((d) => ({ ...d }));

  const Sparkline = ({ points, stroke }) => (
    <ReResponsiveContainer width="100%" height={32}>
      <ReLineChart data={points} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
        <ReLine type="monotone" dataKey="amount" stroke={stroke} strokeWidth={2} dot={false} isAnimationActive={false} />
      </ReLineChart>
    </ReResponsiveContainer>
  );

  return (
    <div className="space-y-4" data-testid="cc-store-insights">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            Quick Insights · Store
          </p>
          <p className="text-sm font-semibold text-[#1A1D1C] truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {data.store?.store_name || '—'}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-[#78716C] hover:text-[#0A2540] px-2 py-1 rounded-md border border-[#EDE6D8] hover:border-[#0A2540]/30 transition"
          data-testid="store-insights-close"
        >
          ← Back to logs
        </button>
      </div>

      {/* Hero tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <HeroTile label="Today's Sales" value={fmt(todayAmt)} unit={currency} icon={CurrencyDollar} accent="#0A2540" delta={delta} deltaUp={deltaUp} deltaLabel="vs yesterday">
          <Sparkline points={series7} stroke="#0A2540" />
        </HeroTile>
        <HeroTile label="This Month" value={fmt(monthAmt)} unit={currency} icon={ChartLineUp} accent="#0A2540" delta={monthDelta} deltaUp={monthDeltaUp} deltaLabel="vs last month">
          <Sparkline points={series30} stroke="#0A2540" />
        </HeroTile>
        <HeroTile label="Volume Today" value={fmt(data.today?.volume || 0)} unit="L" icon={DropIcon} accent="#0A2540" sub={`Month: ${fmt(data.month?.volume || 0)} L`}>
          <Sparkline points={series7.map((d) => ({ amount: d.volume }))} stroke="#0A2540" />
        </HeroTile>
      </div>

      {/* Past 7 days bar */}
      <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#0A2540]/10 text-[#0A2540]"><ChartLineUp size={14} weight="fill" /></span>
            <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              Sales Trend · Past 7 Days
            </p>
          </div>
          <p className="text-[11px] font-mono text-[#78716C]">in {currency}</p>
        </div>
        {series7.every((d) => !d.amount) ? (
          <div className="h-[220px] flex items-center justify-center"><p className="text-xs text-[#78716C]">No sales in the last 7 days.</p></div>
        ) : (
          <div className="h-[220px]">
            <ReResponsiveContainer width="100%" height="100%">
              <ReBarChart data={series7} margin={{ top: 16, right: 8, left: -8, bottom: 0 }}>
                <ReCartesianGrid strokeDasharray="3 3" stroke="#EDE6D8" vertical={false} />
                <ReXAxis dataKey="label" tick={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }} stroke="#78716C" axisLine={false} tickLine={false} />
                <ReYAxis tick={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }} stroke="#78716C" axisLine={false} tickLine={false} tickFormatter={compact} />
                <ReTooltip
                  contentStyle={tooltipStyle}
                  labelStyle={{ color: '#0A2540', fontWeight: 600, marginBottom: 4 }}
                  formatter={(v, _n, ctx) => [`${fmt(v)} ${currency} · ${fmtI(ctx?.payload?.txns)} txn`, 'Sales']}
                />
                <ReBar dataKey="amount" radius={[8, 8, 0, 0]} maxBarSize={64}>
                  {series7.map((_, i) => (<ReCell key={i} fill={i === series7.length - 1 ? '#0A2540' : '#C9A35B'} />))}
                </ReBar>
              </ReBarChart>
            </ReResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

/* Small hero tile with sparkline + delta */
function HeroTile({ label, value, unit, icon: Icon, accent = '#0A2540', delta, deltaUp, deltaLabel, sub, children }) {
  return (
    <div className="rounded-2xl border border-[#EDE6D8] bg-white p-4 relative overflow-hidden">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</p>
          <p className="mt-1.5 text-xl font-bold font-mono leading-tight truncate" style={{ color: accent }}>
            {value}
            {unit && <span className="ml-1 text-[10px] font-semibold text-[#78716C]">{unit}</span>}
          </p>
          {delta != null && (
            <div className="mt-1 flex items-center gap-1">
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold font-mono ${deltaUp ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                {deltaUp ? '▲' : '▼'} {Math.abs(delta).toFixed(0)}%
              </span>
              {deltaLabel && <span className="text-[10px] text-[#78716C] font-mono">{deltaLabel}</span>}
            </div>
          )}
          {sub && <p className="text-[10px] text-[#78716C] mt-1 font-mono truncate">{sub}</p>}
        </div>
        <span className="w-9 h-9 rounded-xl shrink-0 flex items-center justify-center" style={{ backgroundColor: `${accent}1A`, color: accent }}>
          <Icon size={16} weight="fill" />
        </span>
      </div>
      {children && <div className="mt-2">{children}</div>}
    </div>
  );
}

/* ---------- Stores ---------- */
function StoresTab({ campusId, currency }) {
  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(true);

  // Currently-selected store (triggers right-panel switch from logs → insights)
  const [selectedStoreId, setSelectedStoreId] = useState(null);

  // Wallet logs — latest 20 refills (debits only)
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingStores(true);
      try {
        const res = await axios.get(`${API_URL}/api/stores?campus_id=${campusId}`, { headers: authHeaders() });
        if (!cancelled) setStores(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e, 'Failed to load stores'));
      } finally { if (!cancelled) setLoadingStores(false); }
    })();
    return () => { cancelled = true; };
  }, [campusId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLogsLoading(true);
      try {
        const res = await axios.get(
          `${API_URL}/api/wallet-transactions?direction=debit&scope=store&page=1&page_size=20`,
          { headers: authHeaders() }
        );
        if (cancelled) return;
        setLogs(res.data.rows || []);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e, 'Failed to load wallet logs'));
      } finally { if (!cancelled) setLogsLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [campusId]);

  const fmtDate = (s) => {
    if (!s) return '—';
    try {
      // `trn_date` from the middleware is already LOCAL (East Africa) time, so we
      // must display it verbatim — NOT as UTC. Building the Date from explicit
      // local components avoids any browser-timezone shift (previously we wrongly
      // appended 'Z', which pushed every time +3h vs the admin store view).
      const [datePart, timePart = '00:00:00'] = String(s).split(/[ T]/);
      const [y, mo, d] = datePart.split('-').map(Number);
      const [h = 0, mi = 0] = timePart.split(':').map(Number);
      const dt = new Date(y, (mo || 1) - 1, d || 1, h, mi);
      return dt.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch (e) { return s; }
  };

  const selectedStore = stores.find((s) => String(s.id) === String(selectedStoreId));
  // (selectedStore intentionally unused — the panel fetches its own analytics)
  void selectedStore;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-3" data-testid="cc-stores-grid">
      {/* Stores list — narrower left column */}
      <div className="lg:col-span-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            Stores
          </p>
          <p className="text-[10px] text-[#78716C] font-mono">{fmtI(stores.length)} total</p>
        </div>
        {loadingStores ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <SkelCard key={i} className="p-3">
                <SkelBlock className="h-3 w-32 mb-2" />
                <SkelBlock className="h-3 w-20 mb-3" />
                <SkelBlock className="h-3 w-24" />
              </SkelCard>
            ))}
          </div>
        ) : stores.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#EDE6D8] py-12 px-4 flex flex-col items-center text-center gap-3">
            <Storefront size={28} weight="duotone" className="text-[#0A2540]/30" />
            <p className="text-xs font-medium text-[#1A1D1C]">No stores yet</p>
          </div>
        ) : (
          <div className="max-h-[640px] overflow-y-auto pr-1 space-y-2.5" data-testid="cc-stores-scroll">
            {stores.map((s) => {
              const isActive = String(s.id) === String(selectedStoreId);
              return (
                <button
                  type="button"
                  key={s.id}
                  onClick={() => setSelectedStoreId(String(s.id))}
                  className={`w-full text-left bg-white border rounded-2xl p-3 transition
                    ${isActive
                      ? 'border-[#0A2540] ring-2 ring-[#0A2540]/15'
                      : 'border-[#EDE6D8] hover:border-[#0A2540]/40'
                    }`}
                  data-testid={`store-card-${s.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <p className="text-xs font-semibold text-[#1A1D1C] truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      {s.store_name || '—'}
                    </p>
                    <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap shrink-0
                      ${(s.operational_status || '').toLowerCase() === 'operating'
                        ? 'bg-[#10B981]/10 text-[#10B981]'
                        : 'bg-amber-50 text-amber-700'}`}>
                      {(s.operational_status || 'unknown').replace(/_/g, ' ').slice(0, 12)}
                    </span>
                  </div>
                  <p className="text-[10px] uppercase tracking-widest text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {s.store_code || s.id}
                  </p>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-[10px] text-[#78716C] uppercase tracking-widest" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Price</span>
                    <span className="text-xs text-[#1A1D1C] font-semibold">{fmt(s.selling_price)}</span>
                    <span className="text-[10px] text-[#78716C]">{currency}/L</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Right column: store insights when selected, otherwise wallet debits */}
      <div className="lg:col-span-8">
        {selectedStoreId ? (
          <StoreInsightsPanel
            storeId={selectedStoreId}
            currency={currency}
            onClose={() => setSelectedStoreId(null)}
          />
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Refill Activity · Last 20
              </p>
              <p className="text-[10px] text-[#78716C] font-mono">{fmtI(logs.length)} shown</p>
            </div>
            <div className="bg-white border border-[#EDE6D8] rounded-2xl overflow-hidden">
              {logsLoading ? (
                <div className="divide-y divide-[#EDE6D8]">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="px-4 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <SkelBlock className="h-3 w-32 mb-1.5" />
                        <SkelBlock className="h-3 w-20" />
                      </div>
                      <SkelBlock className="h-4 w-20" />
                    </div>
                  ))}
                </div>
              ) : logs.length === 0 ? (
                <div className="py-16 px-6 flex flex-col items-center text-center gap-2">
                  <DropIcon size={26} weight="duotone" className="text-[#0A2540]/30" />
                  <p className="text-xs text-[#78716C]">No refills in the last 10 days.</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[#FCFAF6] text-[10px] uppercase tracking-widest text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold">When</th>
                          <th className="text-left px-3 py-2 font-semibold">Store</th>
                          <th className="text-left px-3 py-2 font-semibold">Card</th>
                          <th className="text-right px-3 py-2 font-semibold">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="text-[#1A1D1C]">
                        {logs.map((r) => (
                          <tr key={r.id || r.trn_ref_no} className="border-t border-[#EDE6D8]" data-testid={`refill-log-${r.id}`}>
                            <td className="px-3 py-2.5 text-[#78716C] whitespace-nowrap" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                              {fmtDate(r.trn_date)}
                            </td>
                            <td className="px-3 py-2.5">
                              <p className="font-medium truncate max-w-[160px]">{r.store_name || '—'}</p>
                            </td>
                            <td className="px-3 py-2.5 text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                              …{(r.card_no || '').slice(-6)}
                            </td>
                            <td className="px-3 py-2.5 text-right text-[#EF4444] font-semibold whitespace-nowrap">
                              {fmt(Math.abs(Number(r.amount || 0)))} {r.currency || currency}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------- Royalties ---------- */
function RoyaltiesTab({ campusId }) {
  return <RoyaltyPanel scope="campus" campusId={campusId} testIdPrefix="cc-royalty" />;
}

/* ---------- Utilities ---------- */
function UtilitiesTab({ campusId, currency }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/campuses/${campusId}/utility-bills`, { headers: authHeaders() });
        if (!cancelled) setRows(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e, 'Failed to load utility bills'));
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [campusId]);

  if (loading) return <div className="py-16 flex items-center justify-center"><Spinner size={20} className="animate-spin text-[#0A2540]" /></div>;
  if (rows.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-[#EDE6D8] py-16 px-6 flex flex-col items-center text-center gap-3">
        <Drop size={32} weight="duotone" className="text-[#0A2540]/30" />
        <p className="text-sm font-medium text-[#1A1D1C]">No utility readings logged for this campus yet.</p>
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#EDE6D8] rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#FCFAF6] text-[10px] uppercase tracking-widest text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Month</th>
              <th className="text-left px-4 py-3 font-semibold">Store</th>
              <th className="text-left px-4 py-3 font-semibold">Type</th>
              <th className="text-right px-4 py-3 font-semibold">Prev</th>
              <th className="text-right px-4 py-3 font-semibold">Reading</th>
              <th className="text-right px-4 py-3 font-semibold">Usage</th>
              <th className="text-right px-4 py-3 font-semibold">Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#EDE6D8]">
                <td className="px-4 py-3 text-[#78716C] text-xs font-mono">{r.billing_month}</td>
                <td className="px-4 py-3 text-[#1A1D1C]">{r.store_name || '—'}</td>
                <td className="px-4 py-3 text-[#1A1D1C] capitalize">{r.bill_type}</td>
                <td className="px-4 py-3 text-right text-[#1A1D1C]">{fmt(r.prev_reading)}</td>
                <td className="px-4 py-3 text-right text-[#1A1D1C]">{fmt(r.meter_reading)}</td>
                <td className="px-4 py-3 text-right text-[#1A1D1C]">{fmt(r.usage)}</td>
                <td className="px-4 py-3 text-right text-[#1A1D1C] font-semibold">{fmt(r.amount)} {currency}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Invoices (utility & royalty: create + track) ---------- */
function PaymentsTab({ campusId, currency }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [billingMonth, setBillingMonth] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [invoiceType, setInvoiceType] = useState('utility');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const handleDownload = async (inv) => {
    setDownloadingId(inv.id);
    try {
      const res = await axios.get(
        `${API_URL}/api/campuses/${campusId}/invoices/${inv.id}/pdf`,
        { headers: authHeaders(), responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.invoice_no || 'invoice'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to download invoice'));
    } finally {
      setDownloadingId(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/campuses/${campusId}/invoices`, { headers: authHeaders() });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) { toast.error(formatApiError(e, 'Failed to load invoices')); }
    finally { setLoading(false); }
  }, [campusId]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!/^\d{4}-\d{2}$/.test(billingMonth)) { toast.error('Pick a valid month (YYYY-MM)'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/campuses/${campusId}/invoices`,
        { billing_month: billingMonth, notes, invoice_type: invoiceType },
        { headers: authHeaders() });
      toast.success('Invoice created');
      setDlgOpen(false); setNotes('');
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const setStatus = async (row, newStatus) => {
    const paidOn = newStatus === 'paid' ? new Date().toISOString().slice(0, 10) : '';
    try {
      await axios.patch(`${API_URL}/api/campuses/${campusId}/invoices/${row.id}`,
        { status: newStatus, paid_on: paidOn }, { headers: authHeaders() });
      toast.success(`Marked as ${newStatus}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const StatusBadge = ({ status }) => {
    const cfg = status === 'paid'
      ? { c: 'bg-[#10B981]/10 text-[#10B981]', Ic: CheckCircle, label: 'Paid' }
      : { c: 'bg-amber-50 text-amber-700', Ic: WarningCircle, label: 'Issued' };
    const Ic = cfg.Ic;
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-semibold ${cfg.c}`}>
        <Ic size={10} weight="bold" /> {cfg.label}
      </span>
    );
  };

  const TypeBadge = ({ type }) => {
    const isRoyalty = type === 'royalty';
    const cls = isRoyalty
      ? 'bg-[#C9A35B]/15 text-[#7A5A1F] ring-[#C9A35B]/40'
      : 'bg-blue-50 text-blue-700 ring-blue-200';
    return (
      <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full ring-1 ring-inset uppercase tracking-widest ${cls}`}>
        {isRoyalty ? 'Royalty' : 'Utility'}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <Button size="sm" onClick={() => setDlgOpen(true)} data-testid="raise-invoice-btn">
          <Plus size={14} className="mr-1" /> Create Invoice
        </Button>
      </div>

      {loading ? (
        <div className="py-16 flex items-center justify-center"><Spinner size={20} className="animate-spin text-[#0A2540]" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#EDE6D8] py-16 px-6 flex flex-col items-center text-center gap-3">
          <Receipt size={32} weight="duotone" className="text-[#0A2540]/30" />
          <p className="text-sm font-medium text-[#1A1D1C]">No invoices yet</p>
          <p className="text-xs text-[#78716C] max-w-sm">Use <b>Create Invoice</b> to consolidate a month of utility bills or royalty records.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#EDE6D8] rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FCFAF6] text-[10px] uppercase tracking-widest text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Invoice No.</th>
                  <th className="text-left px-4 py-3 font-semibold">Type</th>
                  <th className="text-left px-4 py-3 font-semibold">Month</th>
                  <th className="text-right px-4 py-3 font-semibold">Total</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold">Updated</th>
                  <th className="text-right px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-[#EDE6D8]" data-testid={`invoice-row-${r.id}`}>
                    <td className="px-4 py-3 text-[#0A2540] font-mono text-xs">{r.invoice_no || r.id}</td>
                    <td className="px-4 py-3"><TypeBadge type={r.invoice_type || 'utility'} /></td>
                    <td className="px-4 py-3 text-[#1A1D1C]">{r.billing_month_label || r.billing_month}</td>
                    <td className="px-4 py-3 text-right text-[#1A1D1C] font-semibold">{fmt(r.total)} {currency}</td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-3 text-[#78716C] text-xs">
                      {r.status_updated_at ? new Date(r.status_updated_at).toLocaleDateString() : new Date(r.created_at || '').toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownload(r)}
                          disabled={downloadingId === r.id}
                          data-testid={`download-invoice-${r.id}`}
                          title="Download PDF"
                        >
                          {downloadingId === r.id
                            ? <Spinner size={14} className="animate-spin" />
                            : <FilePdf size={14} />}
                        </Button>
                        {r.status === 'paid' ? (
                          <span className="text-[10px] uppercase tracking-widest text-[#78716C] ml-1">Closed</span>
                        ) : (
                          <Button size="sm" onClick={() => setStatus(r, 'paid')} data-testid={`mark-paid-${r.id}`}>
                            Mark Paid
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-w-md" data-testid="raise-invoice-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Create New Invoice</DialogTitle>
            <DialogDescription>
              Choose an invoice type and billing month. The system aggregates all matching records into one consolidated invoice. Only one invoice per type, per month is allowed.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-[#78716C]">Invoice Type</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: 'utility',  l: 'Utility Bills' },
                  { v: 'royalty',  l: 'Royalties' },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setInvoiceType(opt.v)}
                    data-testid={`invoice-type-${opt.v}`}
                    className={`text-sm px-3 py-2 rounded-lg border transition font-medium ${
                      invoiceType === opt.v
                        ? 'bg-[#0A2540] text-white border-[#0A2540]'
                        : 'bg-white text-[#1A1D1C] border-[#EDE6D8] hover:border-[#0A2540]/40'
                    }`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-[#78716C]">Billing Month</Label>
              <Input type="month" value={billingMonth} onChange={(e) => setBillingMonth(e.target.value)} required data-testid="billing-month" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-[#78716C]">Notes (optional)</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any context for HQ…" data-testid="invoice-notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDlgOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="invoice-submit">
                {saving ? 'Creating…' : 'Create Invoice'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Page Shell ---------- */
export default function CampusCorrespondentPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const onb = useOnboarding();
  const [tab, setTab] = useState('overview');

  const campusId = user?.campus_id;
  const campusName = user?.campus_name || 'Campus';
  const currency = useMemo(() => '', []); // resolved from analytics tile if needed

  // Resolve currency once from analytics endpoint (lightweight)
  const [currencyCode, setCurrencyCode] = useState('');
  useEffect(() => {
    if (!campusId) return;
    let cancelled = false;
    (async () => {
      try {
        const me = await axios.get(`${API_URL}/api/auth/me`, { headers: authHeaders() });
        const entityId = me.data.entity_id;
        if (!entityId) return;
        const res = await axios.get(`${API_URL}/api/entities`, { headers: authHeaders() });
        const entity = (res.data || []).find((e) => String(e.id) === String(entityId) || String(e.middleware_id) === String(entityId));
        if (entity && !cancelled) setCurrencyCode(entity.currency_code || '');
      } catch (e) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [campusId]);

  if (!user) return null;
  if (!campusId) {
    return (
      <div className="min-h-screen bg-[#FCFAF6] flex items-center justify-center px-4">
        <div className="rounded-2xl bg-white border border-[#EDE6D8] p-8 max-w-md text-center shadow-sm">
          <WarningCircle size={32} weight="duotone" className="text-amber-500 mx-auto mb-3" />
          <p className="text-sm font-semibold text-[#1A1D1C] mb-1">No campus assigned</p>
          <p className="text-xs text-[#78716C] mb-4">Your account has no campus pinned. Please contact your entity admin.</p>
          <Button variant="outline" size="sm" onClick={logout} data-testid="cc-no-campus-logout">Sign out</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFAF6]" data-testid="campus-correspondent-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#0A2540] text-white border-b border-[#0A2540]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-9 h-9 rounded-xl bg-[#C9A35B]/20 text-[#C9A35B] flex items-center justify-center shrink-0">
              <GraduationCap size={18} weight="duotone" />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Campus Correspondent
              </p>
              <p className="text-sm font-semibold truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {campusName}
              </p>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/15 flex items-center justify-center font-semibold text-xs"
                data-testid="cc-avatar"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                {(user.fullname || user.username || 'U').slice(0, 1).toUpperCase()}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[200px]">
              <DropdownMenuLabel>
                <p className="text-xs font-semibold text-[#1A1D1C] truncate">{user.fullname || user.username}</p>
                <p className="text-[10px] text-[#78716C] truncate">{user.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate('/change-password')}
                data-testid="cc-change-password"
                className="cursor-pointer text-[#0A2540] focus:bg-[#0A2540]/5"
              >
                <LockKey size={14} className="mr-2" /> Change password
              </DropdownMenuItem>
              {onb?.canShowSetupGuide && (
                <DropdownMenuItem
                  onClick={() => onb.launch()}
                  data-testid="cc-setup-guide"
                  className="cursor-pointer text-[#0A2540] focus:bg-[#0A2540]/5"
                >
                  <Compass size={14} className="mr-2" /> Setup Guide
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={logout}
                data-testid="cc-logout"
                className="text-[#EF4444] focus:text-[#EF4444] focus:bg-[#EF4444]/10 cursor-pointer"
              >
                <SignOut size={14} className="mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList className="bg-white border border-[#EDE6D8] rounded-xl p-1 h-auto flex flex-wrap">
            {[
              { v: 'overview',   l: 'Overview',   Ic: ChartPieSlice },
              { v: 'stores',     l: 'Stores',     Ic: Storefront },
              { v: 'royalties',  l: 'Royalties',  Ic: Money },
              { v: 'utilities',  l: 'Utilities',  Ic: Drop },
              { v: 'payments',   l: 'Invoices',   Ic: Receipt },
            ].map(({ v, l, Ic }) => (
              <TabsTrigger
                key={v}
                value={v}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white"
                data-testid={`cc-tab-${v}`}
              >
                <Ic size={14} weight="duotone" /> {l}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="rounded-2xl border border-[#EDE6D8] bg-white/40 p-3 sm:p-4 min-h-[400px]">
            <TabsContent value="overview"   className="mt-0"><OverviewTab    campusId={campusId} currency={currencyCode} /></TabsContent>
            <TabsContent value="stores"     className="mt-0"><StoresTab      campusId={campusId} currency={currencyCode} /></TabsContent>
            <TabsContent value="royalties"  className="mt-0"><RoyaltiesTab   campusId={campusId} currency={currencyCode} /></TabsContent>
            <TabsContent value="utilities"  className="mt-0"><UtilitiesTab   campusId={campusId} currency={currencyCode} /></TabsContent>
            <TabsContent value="payments"   className="mt-0"><PaymentsTab    campusId={campusId} currency={currencyCode} /></TabsContent>
          </div>
        </Tabs>
      </main>
    </div>
  );
}
